import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

const LogDetailModal = ({ open, onClose, logData }) => {
  if (!logData) return null;

  const formatTimestamp = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch (error) {
      return timestamp;
    }
  };

  const safeRender = (value, fallback = 'N/A') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'error': return 'error';
      case 'warn': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Activity Log Details</Typography>
        </Box>
        <Button 
          onClick={onClose} 
          sx={{ color: 'white', minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Basic Information */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Basic Information</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Timestamp</Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatTimestamp(logData.timestamp)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Action</Typography>
              <Typography variant="body1" fontWeight="medium">
                {safeRender(logData.action)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Collection</Typography>
              <Typography variant="body1" fontWeight="medium">
                {safeRender(logData.collectionName)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Document ID</Typography>
              <Typography variant="body1" fontFamily="monospace" fontSize="0.875rem">
                {safeRender(logData.documentId)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip 
                  label={safeRender(logData.status)} 
                  color={getStatusColor(logData.status)} 
                  size="small" 
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Level</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip 
                  label={safeRender(logData.level)} 
                  color={getLevelColor(logData.level)} 
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* User Information */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">User Information</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">User ID</Typography>
              <Typography variant="body1" fontFamily="monospace" fontSize="0.875rem">
                {safeRender(logData.userId)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">
                {safeRender(logData.userEmail)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Office</Typography>
              <Typography 
                variant="body1" 
                component="pre"
                sx={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {safeRender(logData.userOffice)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Designation</Typography>
              <Typography 
                variant="body1"
                component="pre"
                sx={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {safeRender(logData.userDesignation)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Role</Typography>
              <Typography 
                variant="body1"
                component="pre"
                sx={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {safeRender(logData.userRole)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Device Information */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ComputerIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Device Information</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">IP Address</Typography>
              <Typography variant="body1" fontFamily="monospace">
                {safeRender(logData.ipAddress)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Device Type</Typography>
              <Typography variant="body1">
                {safeRender(logData.device)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Browser</Typography>
              <Typography variant="body1">
                {safeRender(logData.browser)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Operating System</Typography>
              <Typography variant="body1">
                {safeRender(logData.os)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Message */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Message</Typography>
          </Box>
          <Typography variant="body1">
            {safeRender(logData.message)}
          </Typography>
        </Paper>

        {/* Changes */}
        {logData.changes && (
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Changes</Typography>
            </Box>
            <Box sx={{ 
              maxHeight: 300, 
              overflow: 'auto', 
              bgcolor: 'white',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <pre style={{ 
                fontSize: '12px', 
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace'
              }}>
                {JSON.stringify(logData.changes, null, 2)}
              </pre>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogDetailModal;

